"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { ArrowsLeftRight, ListChecks, Users, Compass, type IconProps } from "@phosphor-icons/react";

import { Link } from "@/navigation";

const FEATURE_KEYS = [
	{ icon: ArrowsLeftRight, titleKey: "whyChooseFeature1Title", descKey: "whyChooseFeature1Desc" },
	{ icon: ListChecks, titleKey: "whyChooseFeature2Title", descKey: "whyChooseFeature2Desc" },
	{ icon: Users, titleKey: "whyChooseFeature3Title", descKey: "whyChooseFeature3Desc" },
	{ icon: Compass, titleKey: "whyChooseFeature4Title", descKey: "whyChooseFeature4Desc" },
] as const;

const iconSize = 22;
const iconProps: IconProps = { size: iconSize, weight: "fill", className: "text-themePrimary400" };

const CTA_IMAGES = [
	"/image/new-version/casting/Metal-Casting/Why-Choose/metal-casting-1.webp",
	"/image/new-version/casting/Metal-Casting/Why-Choose/metal-casting-2.webp",
];

export default function MetalCastingWhyChoose() {
	const t = useTranslations("new-metal-casting");

	return (
		<section className="w-full bg-white">
			<div className="container py-8 sm:py-10 md:py-12 lg:py-16">
				<h2 className="mb-6 text-center text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
					{t("whyChooseTitle")}
				</h2>

				<div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-3 md:gap-6">
					{FEATURE_KEYS.slice(0, 3).map((item) => {
						const Icon = item.icon;
						return (
							<div
								key={item.titleKey}
								className="flex flex-col border border-[#e2e2e2] bg-white p-5 text-left sm:p-6"
							>
								<div className="mb-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-themePrimary400 bg-[#f0f9eb]">
									<Icon {...iconProps} />
								</div>
								<h3 className="mb-2 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(item.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(item.descKey)}</p>
							</div>
						);
					})}

					{(() => {
						const item = FEATURE_KEYS[3];
						const Icon = item.icon;
						return (
							<div className="flex flex-col border border-[#e2e2e2] bg-white p-5 text-left sm:p-6 md:row-start-2">
								<div className="mb-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-themePrimary400 bg-[#f0f9eb]">
									<Icon {...iconProps} />
								</div>
								<h3 className="mb-2 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(item.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(item.descKey)}</p>
							</div>
						);
					})()}

					<div className="relative min-h-[280px] overflow-hidden px-5 py-10 sm:min-h-[300px] sm:px-6 sm:py-12 md:col-span-2 md:row-start-2">
						<div className="pointer-events-none absolute inset-0 flex justify-around">
							<div className="relative h-full w-1/3">
								<Image
									src={CTA_IMAGES[0]}
									alt=""
									fill
									className="object-cover object-center"
									sizes="(max-width: 768px) 50vw, 33vw"
								/>
							</div>
							<div className="relative h-full w-1/3">
								<Image
									src={CTA_IMAGES[1]}
									alt=""
									fill
									className="object-cover object-center"
									sizes="(max-width: 768px) 50vw, 33vw"
								/>
							</div>
						</div>
						<div className="pointer-events-none absolute inset-0 bg-black/45" />
						<div className="relative flex h-full min-h-[240px] flex-col items-center justify-center text-center">
							<h3 className="mb-2 text-xl font-bold text-white sm:text-2xl md:text-3xl">
								{t("whyChooseCtaTitle")}
							</h3>
							<p className="mb-6 text-sm text-white/90">{t("whyChooseCtaSubtitle")}</p>
							<Link
								href="/upload"
								className="rounded-xl bg-themePrimary400 px-8 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#83b751] hover:text-white"
							>
								{t("whyChooseCtaButton")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
